import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";

// Components (loaded immediately for layout)
import Navbar from "./components/Layout/Navbar";
import Footer from "./components/Layout/Footer";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import FaqChatbot from "./components/Chatbot/FaqChatbot";

// Pages (lazy loaded for better performance)
const Home = lazy(() => import("./pages/Home"));
const Packages = lazy(() => import("./pages/Packages"));
const Payment = lazy(() => import("./pages/Payment"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const Manage = lazy(() => import("./pages/Manage"));
const ManageUsers = lazy(() => import("./pages/ManageUsers"));
const ManagePackages = lazy(() => import("./pages/ManagePackages"));
const ManageClasses = lazy(() => import("./pages/ManageClasses"));
const ManageEmployees = lazy(() => import("./pages/ManageEmployees"));
const EmployeeAttendance = lazy(() => import("./pages/EmployeeAttendance"));
const EmployeeContentTasks = lazy(() => import("./pages/EmployeeContentTasks"));
const AdminContentView = lazy(() => import("./pages/admin/AdminContentView"));
const Analytics = lazy(() => import("./pages/Analytics"));
const AdminQuizManagement = lazy(() => import("./pages/AdminQuizManagement"));
const AdminFaqManagement = lazy(() => import("./pages/AdminFaqManagement"));
const StudentPortal = lazy(() => import("./pages/StudentPortal"));
const Profile = lazy(() => import("./pages/Profile"));
const ProfileDemo = lazy(() => import("./pages/ProfileDemo"));
const QuizPage = lazy(() => import("./pages/QuizPage"));
const QuizAnalysis = lazy(() => import("./pages/QuizAnalysis"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const SiteSettings = lazy(() => import("./pages/SiteSettings"));
const ManageBatches = lazy(() => import("./pages/ManageBatches"));
const BatchDetail = lazy(() => import("./pages/BatchDetail"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const BlogsPage = lazy(() => import("./pages/BlogsPage"));
const BlogDetailPage = lazy(() => import("./pages/BlogDetailPage"));
const ManageBlogs = lazy(() => import("./pages/admin/ManageBlogs"));
const BlogEditor = lazy(() => import("./pages/admin/BlogEditor"));
const ManageStudentFeedback = lazy(
  () => import("./pages/ManageStudentFeedback"),
);
const StudentFeedbackPage = lazy(() => import("./pages/StudentFeedbackPage"));
const LoginSignup = lazy(() => import("./pages/LoginSignup"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));

// Styles
// import "./index.css";

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Component to scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}
function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <Router>
              <ScrollToTop />
              <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col transition-colors duration-300">
                <Navbar />
                <main className="flex-1">
                  <Suspense
                    fallback={
                      <div className="flex items-center justify-center min-h-screen">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                      </div>
                    }
                  >
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/login" element={<LoginSignup />} />
                      <Route path="/signup" element={<LoginSignup />} />
                      <Route path="/packages" element={<Packages />} />
                      <Route path="/about" element={<AboutUs />} />
                      <Route path="/payment" element={<Payment />} />
                      <Route
                        path="/payment-success"
                        element={<PaymentSuccess />}
                      />
                      <Route
                        path="/privacy-policy"
                        element={<PrivacyPolicy />}
                      />
                      <Route
                        path="/terms-of-service"
                        element={<TermsOfService />}
                      />
                      <Route path="/cookie-policy" element={<CookiePolicy />} />
                      <Route path="/blogs" element={<BlogsPage />} />
                      <Route path="/blogs/:slug" element={<BlogDetailPage />} />
                      <Route
                        path="/student-feedback"
                        element={<StudentFeedbackPage />}
                      />
                      <Route path="/quiz" element={<QuizPage />} />
                      <Route
                        path="/quiz/analysis/:sessionId"
                        element={
                          <ProtectedRoute>
                            <QuizAnalysis />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/profile-demo" element={<ProfileDemo />} />
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <Dashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>
                            <Profile />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin"
                        element={
                          <ProtectedRoute requireAdmin>
                            <AdminPanel />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/manage"
                        element={
                          <ProtectedRoute requireTeacherOrEmployee>
                            <Manage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/manage/students"
                        element={
                          <ProtectedRoute requireAdmin>
                            <ManageUsers />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/manage/users"
                        element={
                          <ProtectedRoute
                            requireAdmin
                            requireClaim="manage_users"
                          >
                            <ManageUsers />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/manage/packages"
                        element={
                          <ProtectedRoute
                            requireAdmin
                            requireClaim="manage_packages"
                          >
                            <ManagePackages />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/manage/classes"
                        element={
                          <ProtectedRoute
                            requireAdminOrTeacher
                            requireClaim="manage_classes"
                          >
                            <ManageClasses />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/manage/employees"
                        element={
                          <ProtectedRoute
                            requireAdmin
                            requireClaim="manage_employees"
                          >
                            <ManageEmployees />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/employee/attendance"
                        element={
                          <ProtectedRoute requireTeacherOrEmployee>
                            <EmployeeAttendance />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/employee/tasks"
                        element={
                          <ProtectedRoute requireTeacherOrEmployee>
                            <EmployeeContentTasks />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/manage/content-view"
                        element={
                          <ProtectedRoute
                            requireAdmin
                            requireClaim="admin_content_view"
                          >
                            <AdminContentView />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/manage/quiz"
                        element={
                          <ProtectedRoute
                            requireAdmin
                            requireClaim="manage_quizzes"
                          >
                            <AdminQuizManagement />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/manage/faqs"
                        element={
                          <ProtectedRoute
                            requireAdmin
                            requireClaim="manage_faqs"
                          >
                            <AdminFaqManagement />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/manage/student-feedback"
                        element={
                          <ProtectedRoute
                            requireTeacherOrEmployee
                            requireClaim="manage_student_feedback"
                          >
                            <ManageStudentFeedback />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/manage/analytics"
                        element={
                          <ProtectedRoute
                            requireAdmin
                            requireClaim="manage_analytics"
                          >
                            <Analytics />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/manage/settings"
                        element={
                          <ProtectedRoute
                            requireAdmin
                            requireClaim="site_settings"
                          >
                            <SiteSettings />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/manage/batches"
                        element={
                          <ProtectedRoute
                            requireAdmin
                            requireClaim="manage_batches"
                          >
                            <ManageBatches />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/manage/batches/:id"
                        element={
                          <ProtectedRoute requireAdmin>
                            <BatchDetail />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/blogs"
                        element={
                          <ProtectedRoute requireAdmin>
                            <ManageBlogs />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/blogs/:id"
                        element={
                          <ProtectedRoute requireAdmin>
                            <BlogEditor />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/student/:id" element={<StudentPortal />} />
                      <Route path="/auth/callback" element={<AuthCallback />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </main>
                <Footer />
                <FaqChatbot />
              </div>
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: "#363636",
                    color: "#fff",
                  },
                }}
              />
            </Router>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
